import { fetchJSONWithTimeout } from '@noths/polaris-client-utils';

import { API_URLS } from '../common/config';
import type { ListingsAPIErrorResponseBody } from '../common/types/ListingsAPIErrorResponseBody';
import type {
  RecommendationsRequestBody,
  RecommendationsRequestHeaders,
} from './types/RecommendationsRequestBody';
import type { RecommendationsResponseBody } from './types/RecommendationsResponseBody';

const DEFAULT_TIMEOUT = 5000;

export type FetchRecommendationsDataArgs = {
  body: RecommendationsRequestBody;
  clientSide?: boolean;
  env: 'qa' | 'prod';
  /**
   * Names the Vertex serving config, sent as the `noths-serving-config` header.
   *
   * A `string` rather than `RecommendationsServingConfig`, because a consumer
   * whose config comes from its own environment has no literal for the union to
   * check, and the alternative is a cast at the call site that would accept a
   * typo just as readily while looking as if something had verified it. This
   * function only puts the value in a header, so it is not the place the check
   * belongs: a surface naming its config in code should type it as
   * `RecommendationsServingConfig`, which the three carousels in this repo do,
   * and one reading it from config should validate it where it reads it.
   */
  servingConfig?: string;
  timeout?: number;
  // Full request URL, overriding the built-in one for `env`. Lets a consumer
  // drive the URL from its own config rather than this package's defaults.
  url?: string;
} & Pick<RecommendationsRequestHeaders, 'userId' | 'visitorId' | 'validateOnly'>;

export const fetchRecommendationsData = async ({
  body,
  clientSide = false,
  env,
  servingConfig,
  timeout = DEFAULT_TIMEOUT,
  url,
  userId,
  validateOnly = false,
  visitorId,
}: FetchRecommendationsDataArgs): Promise<
  RecommendationsResponseBody | ListingsAPIErrorResponseBody
> => {
  const envConfig = API_URLS.recommendations[env];
  const requestUrl = url ?? (clientSide ? envConfig.clientSide : envConfig.serverSide);
  if (!visitorId) {
    throw new Error('visitorId must be specified');
  }

  const apiResponse = await fetchJSONWithTimeout(requestUrl, {
    method: 'POST',
    timeout,
    headers: {
      ...(userId && { 'noths-user-id': userId }),
      'noths-visitor-id': visitorId,
      'Content-Type': 'application/json',
      ...(servingConfig && { 'noths-serving-config': servingConfig }),
      'validate-only': validateOnly.toString(),
    },
    body: body as unknown as Record<string, unknown>,
  });

  const data = await apiResponse.json();

  if (apiResponse.ok) {
    return data as RecommendationsResponseBody;
  }

  if (data.message) {
    return data as ListingsAPIErrorResponseBody;
  }

  return data;
};
